
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useState } from "react";
import { PinImageUploader } from "./PinImageUploader";
import { ImageUrlInput } from "./ImageUrlInput";
import { AiArtGenerator } from "./AiArtGenerator";
import { toast } from "sonner";
import { Upload, Link2, Sparkles } from "lucide-react";

interface PinFormProps {
  userId: string | null;
  onSubmit: (data: PinFormData) => Promise<void>;
  isLoading: boolean;
}

export interface PinFormData {
  title: string;
  description: string;
  category: string;
  isForSale: boolean;
  imageUrl: string;
}

export const categories = [
  { value: "manga", label: "Mangá" },
  { value: "digital-art", label: "Arte Digital" },
  { value: "illustration", label: "Ilustração" },
  { value: "design", label: "Design" },
  { value: "comics", label: "Quadrinhos" },
  { value: "ai-art", label: "IA Art" },
  { value: "other", label: "Outro" },
];

export function PinForm({ userId, onSubmit, isLoading }: PinFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isForSale, setIsForSale] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imageSource, setImageSource] = useState<"upload" | "url" | "ai">("upload");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!imageUrl) {
      toast.error("Por favor, adicione uma imagem");
      return;
    }

    if (!title) {
      toast.error("Por favor, adicione um título");
      return;
    }

    await onSubmit({
      title,
      description,
      category,
      isForSale,
      imageUrl
    });
  };

  const handleImageSelected = (url: string) => {
    setImageUrl(url);
  };

  // Create a proper Promise function for the onImageChange prop
  const handleImageChange = async (url: string): Promise<void> => {
    setImageUrl(url);
    return Promise.resolve();
  };

  const handleSetCategory = (newCategory: string) => {
    setCategory(newCategory);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs 
        defaultValue="upload" 
        value={imageSource} 
        onValueChange={(value) => setImageSource(value as "upload" | "url" | "ai")}
        className="w-full"
      >
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="upload" className="flex items-center gap-1">
            <Upload size={16} /> Upload
          </TabsTrigger>
          <TabsTrigger value="url" className="flex items-center gap-1">
            <Link2 size={16} /> URL
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-1">
            <Sparkles size={16} /> IA Art
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="mt-0">
          <PinImageUploader 
            onImageSelected={handleImageSelected}
            onImageChange={handleImageChange}
            disabled={isLoading}
            userId={userId || undefined}
          />
        </TabsContent>
        
        <TabsContent value="url" className="mt-0">
          <ImageUrlInput 
            onImageSelected={handleImageSelected}
            disabled={isLoading}
          />
        </TabsContent>
        
        <TabsContent value="ai" className="mt-0">
          <AiArtGenerator 
            onImageGenerated={handleImageSelected}
            onSetCategory={handleSetCategory}
          />
        </TabsContent>
      </Tabs>

      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isLoading || uploading}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descrição</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isLoading || uploading}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Categoria</Label>
        <Select
          value={category}
          onValueChange={setCategory}
          disabled={isLoading || uploading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione uma categoria" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="for-sale"
          checked={isForSale}
          onCheckedChange={setIsForSale}
          disabled={isLoading || uploading}
        />
        <Label htmlFor="for-sale">Disponível para venda</Label>
      </div>

      <Button 
        type="submit" 
        disabled={isLoading || !imageUrl || !title} 
        className="w-full"
      >
        {isLoading ? "Criando..." : "Criar Pin"}
      </Button>
    </form>
  );
}
